import React, { createContext, useReducer, useContext, useEffect } from 'react';

// Initial cart state
const initialState = {
    items: [],
    subtotal: 0,
    tax: 0,
    taxRate: 0.19, // 19% German VAT
    shipping: 0,
    total: 0
};

// Create context
export const CartContext = createContext();

// Cart action types
const ADD_ITEM = 'ADD_ITEM';
const REMOVE_ITEM = 'REMOVE_ITEM';
const UPDATE_QUANTITY = 'UPDATE_QUANTITY';
const CLEAR_CART = 'CLEAR_CART';
const UPDATE_TOTALS = 'UPDATE_TOTALS';

// Reducer for cart operations
const cartReducer = (state, action) => {
    switch (action.type) {
        case ADD_ITEM: {
            // Ensure payload is valid
            if (!action.payload || typeof action.payload !== 'object') {
                return state;
            }

            const newItem = action.payload;
            const quantity = newItem.quantity || 1; // Default to 1 if quantity is not provided
            
            // Check if item already exists
            const existingItemIndex = state.items.findIndex(
                item => item && item.id === newItem.id
            );
            
            let updatedItems;
            if (existingItemIndex >= 0) {
                // Update quantity of existing item
                updatedItems = state.items.map((item, index) => {
                    if (index === existingItemIndex) {
                        const currentQuantity = item.quantity || 0;
                        return { 
                            ...item, 
                            quantity: currentQuantity + quantity 
                        };
                    }
                    return item;
                });
            } else {
                // Add new item with safe quantity
                updatedItems = [...state.items, {
                    ...newItem,
                    quantity: quantity
                }];
            }
            
            return {
                ...state,
                items: updatedItems,
            };
        }
        
        case REMOVE_ITEM: {
            const updatedItems = state.items.filter(item => item && item.id !== action.payload);
            return {
                ...state,
                items: updatedItems,
            };
        }
        
        case UPDATE_QUANTITY: {
            const { id, quantity } = action.payload || {};
            if (!id || quantity < 1) return state;
            
            const updatedItems = state.items.map(item =>
                (item && item.id === id) ? { ...item, quantity } : item
            );
            
            return {
                ...state,
                items: updatedItems,
            };
        }
        
        case CLEAR_CART:
            return {
                ...initialState
            };
            
        case UPDATE_TOTALS: {
            const subtotal = state.items.reduce(
                (sum, item) => {
                    if (!item || typeof item !== 'object') return sum;
                    const price = parseFloat(item.price) || 0;
                    const quantity = parseInt(item.quantity) || 0;
                    return sum + (price * quantity);
                }, 0
            );
            
            const tax = subtotal * state.taxRate; // German VAT
            const shipping = subtotal > 50 ? 0 : 4.95; // Standard German shipping rates
            const total = subtotal + shipping;
            
            return {
                ...state,
                subtotal,
                tax,
                shipping,
                total
            };
        }
        
        default:
            return state;
    }
};

// CartProvider component
export const CartProvider = ({ children }) => {
    const [state, dispatch] = useReducer(cartReducer, initialState);
    
    // Calculate totals whenever items change
    useEffect(() => {
        dispatch({ type: UPDATE_TOTALS });
    }, [state.items]);
    
    // Add item to cart
    const addItem = (item) => {
        if (!item || typeof item !== 'object') return;
        dispatch({ type: ADD_ITEM, payload: item });
    };
    
    // Remove item from cart
    const removeItem = (itemId) => {
        if (!itemId) return;
        dispatch({ type: REMOVE_ITEM, payload: itemId });
    };
    
    // Update item quantity
    const updateQuantity = (id, quantity) => {
        if (!id || quantity < 1) return;
        dispatch({ type: UPDATE_QUANTITY, payload: { id, quantity } });
    };
    
    // Clear cart
    const clearCart = () => {
        dispatch({ type: CLEAR_CART });
    };
    
    return (
        <CartContext.Provider value={{
            cart: state,
            addItem,
            removeItem,
            updateQuantity,
            clearCart
        }}>
            {children}
        </CartContext.Provider>
    );
};

// Custom hook for using the cart context
export const useCart = () => {
    const context = useContext(CartContext);
    if (!context) {
        throw new Error('useCart must be used within a CartProvider');
    }
    return context;
};
